import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  Grid,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Person as PersonIcon,
  Visibility as VisibilityIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Badge as BadgeIcon,
} from '@mui/icons-material';
import { useAuth } from '@hooks/useAuth';
import { getAllUsers, updateUserDocument, leaveOrganization } from '@services/userService';
import { getAllPendingInvites } from '@services/inviteService';
import { getAllPreRegisteredVolunteers } from '@services/preRegisteredVolunteerService';

const USER_TYPES = ['volunteer', 'org-admin', 'platform-admin'];
const STATUSES = ['active', 'pending', 'rejected'];
const ROLES = [{ value: '', label: 'None' }, { value: 'owner', label: 'Owner' }, { value: 'co-admin', label: 'Co-Admin' }];

const typeColor = (t) => {
  if (t === 'platform-admin') return 'error';
  if (t === 'org-admin') return 'primary';
  return 'default';
};

const statusColor = (s) => {
  if (s === 'active') return 'success';
  if (s === 'pending') return 'warning';
  if (s === 'rejected') return 'error';
  return 'default';
};

const AdminUsersPage = () => {
  const navigate = useNavigate();
  const { userData } = useAuth();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Tabs: 0 = Registered Users, 1 = Invited (not yet registered)
  const [tab, setTab] = useState(0);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [pendingPreReg, setPendingPreReg] = useState([]);
  const [invitedLoading, setInvitedLoading] = useState(true);
  const [invitedSearch, setInvitedSearch] = useState('');

  // Filters
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [editForm, setEditForm] = useState({ userType: '', status: '', role: '' });
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState('');

  // View dialog
  const [viewOpen, setViewOpen] = useState(false);
  const [viewUser, setViewUser] = useState(null);

  // Reset org dialog
  const [resetOrgOpen, setResetOrgOpen] = useState(false);
  const [resetOrgUser, setResetOrgUser] = useState(null);
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    if (userData?.userType !== 'platform-admin') {
      navigate('/dashboard');
      return;
    }
    getAllUsers()
      .then(setUsers)
      .catch(() => setError('Failed to load users.'))
      .finally(() => setLoading(false));

    Promise.all([getAllPendingInvites(), getAllPreRegisteredVolunteers()])
      .then(([invites, preReg]) => {
        setPendingInvites(invites);
        setPendingPreReg(preReg);
      })
      .catch(() => setError('Failed to load invited users.'))
      .finally(() => setInvitedLoading(false));
  }, [userData, navigate]);

  // Normalize both "invited but not registered" sources into one list for display
  const invitedRows = useMemo(() => {
    const fromInvites = pendingInvites.map((inv) => ({
      id: `invite-${inv.id}`,
      name: null,
      email: inv.email,
      organizationName: inv.organizationName,
      source: 'Email Invite',
      date: inv.invitedAt,
    }));
    const fromPreReg = pendingPreReg.map((v) => ({
      id: `prereg-${v.id}`,
      name: v.displayName,
      email: v.email,
      organizationName: v.organizationName,
      source: 'Added by Admin',
      date: v.createdAt,
    }));
    return [...fromInvites, ...fromPreReg].sort((a, b) => (b.date?.seconds || 0) - (a.date?.seconds || 0));
  }, [pendingInvites, pendingPreReg]);

  const filteredInvited = useMemo(() => {
    const q = invitedSearch.toLowerCase();
    if (!q) return invitedRows;
    return invitedRows.filter((r) =>
      r.name?.toLowerCase().includes(q) ||
      r.email?.toLowerCase().includes(q) ||
      r.organizationName?.toLowerCase().includes(q)
    );
  }, [invitedRows, invitedSearch]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return users.filter(u => {
      if (q && !u.displayName?.toLowerCase().includes(q) && !u.email?.toLowerCase().includes(q)) return false;
      if (typeFilter && u.userType !== typeFilter) return false;
      if (statusFilter && u.status !== statusFilter) return false;
      return true;
    });
  }, [users, search, typeFilter, statusFilter]);

  // ── Edit ──────────────────────────────────────────────────────────────────
  const openEdit = (user) => {
    setSelected(user);
    setEditForm({ userType: user.userType || '', status: user.status || '', role: user.role || '' });
    setEditError('');
    setEditOpen(true);
  };

  const handleSaveEdit = async () => {
    try {
      setSaving(true);
      setEditError('');
      await updateUserDocument(selected.uid, {
        userType: editForm.userType,
        status: editForm.status,
        role: editForm.role || null,
      });
      setUsers(prev => prev.map(u => u.uid === selected.uid
        ? { ...u, userType: editForm.userType, status: editForm.status, role: editForm.role || null }
        : u
      ));
      setEditOpen(false);
    } catch {
      setEditError('Failed to save changes.');
    } finally {
      setSaving(false);
    }
  };

  // ── View / Assist ─────────────────────────────────────────────────────────
  const openView = (user) => {
    setViewUser(user);
    setViewOpen(true);
  };

  // ── Reset Org ─────────────────────────────────────────────────────────────
  const openResetOrg = (user) => {
    setResetOrgUser(user);
    setResetOrgOpen(true);
  };

  const handleResetOrg = async () => {
    if (!resetOrgUser?.organizationId) return;
    try {
      setResetting(true);
      await leaveOrganization(resetOrgUser.uid, resetOrgUser.organizationId);
      setUsers(prev => prev.map(u => u.uid === resetOrgUser.uid
        ? { ...u, organizationId: null, organizationName: null, organizations: [], status: null }
        : u
      ));
      setResetOrgOpen(false);
    } catch {
      setError('Failed to reset organization assignment.');
      setResetOrgOpen(false);
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <PersonIcon color="primary" sx={{ fontSize: 32 }} />
        <Box>
          <Typography variant="h4" fontWeight="bold">Users</Typography>
          <Typography variant="body2" color="text.secondary">
            {users.length} user{users.length !== 1 ? 's' : ''} registered
          </Typography>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {/* Tabs */}
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label={`Registered Users (${users.length})`} />
        <Tab label={`Invited (${invitedRows.length})`} />
      </Tabs>

      {tab === 0 && <>
      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          placeholder="Search by name or email…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          size="small"
          sx={{ width: 280 }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
          }}
        />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Type</InputLabel>
          <Select value={typeFilter} label="Type" onChange={e => setTypeFilter(e.target.value)}>
            <MenuItem value="">All types</MenuItem>
            {USER_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} label="Status" onChange={e => setStatusFilter(e.target.value)}>
            <MenuItem value="">All statuses</MenuItem>
            {STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>

      {/* Table */}
      <TableContainer component={Paper} elevation={2}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><strong>Name</strong></TableCell>
              <TableCell><strong>Email</strong></TableCell>
              <TableCell><strong>Organization</strong></TableCell>
              <TableCell><strong>Type</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Role</strong></TableCell>
              <TableCell align="right"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No users found.
                </TableCell>
              </TableRow>
            ) : (
              filtered.map(user => (
                <TableRow key={user.uid} hover>
                  <TableCell>{user.displayName || '—'}</TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>{user.email || '—'}</TableCell>
                  <TableCell sx={{ fontSize: '0.8rem' }}>{user.organizationName || '—'}</TableCell>
                  <TableCell>
                    <Chip label={user.userType || '—'} color={typeColor(user.userType)} size="small" />
                  </TableCell>
                  <TableCell>
                    {user.status
                      ? <Chip label={user.status} color={statusColor(user.status)} size="small" />
                      : '—'}
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>{user.role || '—'}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="View user details">
                      <IconButton size="small" onClick={() => openView(user)}>
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit user">
                      <IconButton size="small" onClick={() => openEdit(user)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      </>}

      {tab === 1 && <>
      {/* Filter */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          placeholder="Search by name, email, or organization…"
          value={invitedSearch}
          onChange={e => setInvitedSearch(e.target.value)}
          size="small"
          sx={{ width: 320 }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
          }}
        />
      </Box>

      {/* Invited table — read-only oversight of people invited/added but not yet registered */}
      <TableContainer component={Paper} elevation={2}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><strong>Name</strong></TableCell>
              <TableCell><strong>Email</strong></TableCell>
              <TableCell><strong>Organization</strong></TableCell>
              <TableCell><strong>Source</strong></TableCell>
              <TableCell><strong>Date</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {invitedLoading ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : filteredInvited.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No one is currently invited but not yet registered.
                </TableCell>
              </TableRow>
            ) : (
              filteredInvited.map(row => (
                <TableRow key={row.id} hover>
                  <TableCell>{row.name || '—'}</TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>{row.email || '—'}</TableCell>
                  <TableCell sx={{ fontSize: '0.8rem' }}>{row.organizationName || '—'}</TableCell>
                  <TableCell>
                    <Chip label={row.source} size="small" color={row.source === 'Email Invite' ? 'info' : 'default'} />
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                    {row.date?.seconds ? new Date(row.date.seconds * 1000).toLocaleDateString() : '—'}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      </>}

      {/* ── Edit Dialog ── */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {selected && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {selected.displayName} · {selected.email}
            </Typography>
          )}
          {editError && <Alert severity="error" sx={{ mb: 2 }}>{editError}</Alert>}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>User Type</InputLabel>
              <Select value={editForm.userType} label="User Type" onChange={e => setEditForm(f => ({ ...f, userType: e.target.value }))}>
                {USER_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select value={editForm.status} label="Status" onChange={e => setEditForm(f => ({ ...f, status: e.target.value }))}>
                {STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Role</InputLabel>
              <Select value={editForm.role} label="Role" onChange={e => setEditForm(f => ({ ...f, role: e.target.value }))}>
                {ROLES.map(r => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
              </Select>
            </FormControl>
            {selected?.organizationId && (
              <>
                <Divider />
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Organization: <strong>{selected.organizationName}</strong>
                  </Typography>
                  <Button
                    size="small"
                    color="error"
                    variant="outlined"
                    onClick={() => { setEditOpen(false); openResetOrg(selected); }}
                  >
                    Reset Organization Assignment
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveEdit} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── View / Assist Dialog ── */}
      <Dialog open={viewOpen} onClose={() => setViewOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>User Details</DialogTitle>
        <DialogContent>
          {viewUser && (
            <Grid container spacing={2} sx={{ mt: 0 }}>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                  <PersonIcon color="primary" sx={{ fontSize: 40 }} />
                  <Box>
                    <Typography variant="h6" fontWeight="bold">{viewUser.displayName || '—'}</Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                      <Chip label={viewUser.userType || '—'} color={typeColor(viewUser.userType)} size="small" />
                      {viewUser.status && <Chip label={viewUser.status} color={statusColor(viewUser.status)} size="small" />}
                      {viewUser.role && <Chip label={viewUser.role} size="small" variant="outlined" />}
                    </Box>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12}><Divider /></Grid>

              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <EmailIcon fontSize="small" color="action" />
                  <Typography variant="body2"><strong>Email</strong></Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">{viewUser.email || '—'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <PhoneIcon fontSize="small" color="action" />
                  <Typography variant="body2"><strong>Phone</strong></Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">{viewUser.phoneNumber || '—'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <BusinessIcon fontSize="small" color="action" />
                  <Typography variant="body2"><strong>Organization</strong></Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">{viewUser.organizationName || '—'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <BadgeIcon fontSize="small" color="action" />
                  <Typography variant="body2"><strong>User ID</strong></Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', fontFamily: 'monospace' }}>
                  {viewUser.uid}
                </Typography>
              </Grid>
              {viewUser.skills?.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Skills</strong></Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {viewUser.skills.map(s => <Chip key={s} label={s} size="small" variant="outlined" />)}
                  </Box>
                </Grid>
              )}
              {viewUser.totalHours !== undefined && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2"><strong>Total Hours</strong></Typography>
                  <Typography variant="body2" color="text.secondary">{viewUser.totalHours || 0} hrs</Typography>
                </Grid>
              )}
              {viewUser.smsOptIn !== undefined && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2"><strong>SMS Opt-In</strong></Typography>
                  <Typography variant="body2" color="text.secondary">{viewUser.smsOptIn ? 'Yes' : 'No'}</Typography>
                </Grid>
              )}
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                  Created: {viewUser.createdAt?.toDate?.()?.toLocaleDateString() || '—'} ·{' '}
                  Updated: {viewUser.updatedAt?.toDate?.()?.toLocaleDateString() || '—'}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewOpen(false)}>Close</Button>
          <Button variant="outlined" onClick={() => { setViewOpen(false); openEdit(viewUser); }}>
            Edit User
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Reset Org Confirm Dialog ── */}
      <Dialog open={resetOrgOpen} onClose={() => setResetOrgOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Reset Organization Assignment</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This will remove <strong>{resetOrgUser?.displayName}</strong> from{' '}
            <strong>{resetOrgUser?.organizationName}</strong> and cancel all their upcoming event registrations in that org.
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetOrgOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleResetOrg} disabled={resetting}>
            {resetting ? 'Resetting…' : 'Reset'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminUsersPage;
